/**
 * Hook for using the generated Neo4j client with prepared queries
 *
 * Usage:
 *   const client = useNeoClient();
 *
 *   // Execute a query
 *   const threads = await client.Thread
 *     .filter({ workspace_id: workspaceId })
 *     .steps.filter({ role: 'user' })
 *     .limit(10)
 *     .execute();
 */

import { useCallback, useMemo } from 'react';
// Import ALL generated query builders dynamically
import * as generatedApi from '@sourcepilot/generated/api-index';
import { ModelRegistry } from '@sourcepilot/generated/registries/model-registry';
import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { QueryBuilderBase } from '@sourcepilot/framework/query-builder-base';
import { performLogout } from '@sourcepilot/ui/shell/auth/use-simple-auth';
// ACTIVE-BRANCH BINDING (COW lens): every backend call names the bound branch
import { branchHeaders } from '@sourcepilot/ui/framework/data/active-branch';
import { activeHolder, readHolderHeaders } from '@sourcepilot/ui/framework/data/active-holder';
import { noteWrite } from '@sourcepilot/ui/framework/data/pins/noted-conflicts';
import { ApiError } from '@sourcepilot/ui/framework/data/api-error';

export { ApiError };

// API client configuration
// Exported so feature pages (e.g. the versions page) share ONE backend-origin
// convention instead of duplicating the fallback (plan.md Phase 0 item 10).
export const API_BASE_URL = process.env.NEXT_PUBLIC_SOURCEPILOT_URL || 'http://localhost:8000';

/**
 * Fetch function that calls the backend API
 */
export async function apiFetch(path: string, options: RequestInit = {}, { atHead = false }: { atHead?: boolean } = {}) {
  const url = `${API_BASE_URL}${path}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...branchHeaders(),
      // a read from a holder's page names it (its references at its pins)
      ...(atHead ? {} : readHolderHeaders(path, options)),
      ...options.headers,
    },
    credentials: 'include', // Include cookies for auth
  });

  // Handle 401 unauthorized - redirect to login
  if (response.status === 401) {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('returnUrl', window.location.pathname);
    }
    await performLogout();
    throw new Error('Authentication required');
  }

  if (!response.ok) {
    const text = await response.text();
    let body: unknown = text;
    try { body = text ? JSON.parse(text) : null; } catch { /* not JSON: keep the text */ }
    throw new ApiError(response.status, body, text);
  }

  const body = await response.json();
  // V33 noted conflicts (VC-D): a create or modify in a draft that left a
  // route conflict says so; it joins this page's holder's noted conflicts,
  // and the page's alert shows the server's warning at once
  const holder = activeHolder();
  if (holder && /^\/api\/(create|modify)(\?|$)/.test(path)) noteWrite(holder, body);
  return body;
}

/** A READ AT HEAD from a holder's page: for a picker that ADDS a link (the
 *  new link pins what the user picked, at head — never the holder's pinned
 *  values). Pass it to a query builder: `.all(apiFetchAtHead)`. */
export function apiFetchAtHead(path: string, options: RequestInit = {}) {
  return apiFetch(path, options, { atHead: true });
}

/**
 * Hook that provides the Neo4j client with all query builders
 *
 * Automatically exposes all generated API classes (Thread, Step, etc.)
 * without needing to hardcode them.
 */
export function useNeoClient() {
  // Bind the API fetch function to the query builders
  const boundFetch = useCallback(apiFetch, []);

  // Create client with all generated classes
  const client = useMemo(() => {
    // Configure API client with PUT for create, PATCH for modify
    const apiClient = {
      put: async (path: string, data: any) => {
        return apiFetch(path, {
          method: 'PUT',  // Backend uses PUT for /create
          body: JSON.stringify(data)
        });
      },
      patch: async (path: string, data: any) => {
        return apiFetch(path, {
          method: 'PATCH',
          body: JSON.stringify(data)
        });
      },
      delete: async (path: string, data: any) => {
        return apiFetch(path, {
          method: 'DELETE',
          body: JSON.stringify(data)
        });
      }
    };

    // Set API client on base class (all models inherit from this)
    NeoNodeBase.setApiClient(apiClient);

    // Set default fetch function for all query builders
    QueryBuilderBase.setDefaultFetch(boundFetch);

    // Inject ModelRegistry to break circular dependency
    QueryBuilderBase.setModelRegistry(ModelRegistry);

    return {
      // Spread all generated API classes (Thread, Step, ModelRequest, etc.)
      ...generatedApi,

      // Helper to execute any query builder
      execute: async <T>(query: any): Promise<T> => {
        return query.execute(boundFetch);
      },

      // Raw fetch for custom queries
      fetch: boundFetch,
    };
  }, [boundFetch]);

  return client;
}

/**
 * Type-safe query result
 */
export interface QueryResult<T> {
  nodes: T[];
  loadContext: Record<string, any>;
}
